import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Clock, Airplane, WarningCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact availability badge used on worker cards and profile headers.
 * Fetches /api/worker-availability/status/{workerId} and renders a colored pill.
 *
 * Props:
 *  - workerId (required)
 *  - size: 'sm' | 'md' (default 'sm')
 */
export default function AvailabilityBadge({ workerId, size = 'sm' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workerId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/worker-availability/status/${workerId}`);
        if (!cancelled && res.data?.status) setStatus(res.data.status);
      } catch {
        /* worker has no availability record yet — default to available */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workerId]);

  if (loading || !status) return null;

  const padding = size === 'md' ? 'px-3 py-1.5 text-xs' : 'px-2 py-1 text-[10px]';
  const iconSize = size === 'md' ? 14 : 12;

  // Vacation mode takes priority
  if (status.vacation_mode && status.vacation_until) {
    const backDate = new Date(status.vacation_until + 'T00:00:00');
    const backLabel = backDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 border border-amber-300 ${padding}`}
        data-testid={`availability-badge-${workerId}`}
        title={`On vacation until ${backLabel}`}
      >
        <Airplane size={iconSize} weight="fill" />
        Back {backLabel}
      </span>
    );
  }

  if (status.available_today) {
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 ${padding}`}
        data-testid={`availability-badge-${workerId}`}
        title="Available today"
      >
        <CheckCircle size={iconSize} weight="fill" />
        Available Today
      </span>
    );
  }

  // Busy today but will be free soon
  const next = new Date(status.next_available_date + 'T00:00:00');
  const nextLabel = next.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide rounded-full bg-neutral-100 text-neutral-700 border border-neutral-300 ${padding}`}
      data-testid={`availability-badge-${workerId}`}
      title={`Booked today — next free ${nextLabel}`}
    >
      <Clock size={iconSize} weight="fill" />
      Free {nextLabel}
    </span>
  );
}
